import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import * as SahabatGadaiData from './data/financialData';
import * as JuaraGadaiData from './data/financialData_juara_gadai';

type DataModule = typeof SahabatGadaiData;

const DataContext = createContext<DataModule>(SahabatGadaiData);

interface DataProviderProps {
  activeEntity: string;
  children: React.ReactNode;
}

// Helper to classify account codes (COA) dynamically in JS
function classifyAccount(no_acc: number | string): [string, string] {
  const code = String(no_acc).trim();
  if (!code || !/^\d+$/.test(code)) {
    return ['UNMAPPED', 'UNMAPPED'];
  }
  const prefix3 = code.slice(0, 3);
  const prefix2 = code.slice(0, 2);

  // ---- Revenue (10xxx) ----
  if (code.startsWith('101')) return ['REVENUE', 'Interest Income'];
  if (code.startsWith('102')) return ['REVENUE', 'Admin Fee'];
  if (code.startsWith('103')) return ['REVENUE', 'Gain Lelang'];
  if (code.startsWith('104')) return ['REVENUE', 'Late Fee'];
  if (code.startsWith('105')) return ['REVENUE', 'Other Interest'];
  if (code.startsWith('106')) return ['REVENUE', 'Accrued Revenue'];

  // ---- COGS (11xxx) ----
  if (code.startsWith('11101')) return ['COGS', 'Repair Lelang'];
  if (code.startsWith('11102')) return ['COGS', 'Freight In'];
  if (code.startsWith('11103')) return ['COGS', 'Bunga'];
  if (prefix3 === '111') return ['COGS', 'Other Direct'];

  // ---- Operating Expenses (12xxx) ----
  if (prefix3 === '121' || prefix3 === '122' || prefix3 === '123' || prefix3 === '124' || prefix3 === '125') {
    return ['OPEX', 'Personnel'];
  }
  if (prefix3 === '126') return ['OPEX', 'Office & Ops'];
  if (prefix3 === '127') return ['OPEX', 'Depresiasi'];
  if (prefix3 === '128') return ['OPEX', 'Travel'];
  if (prefix2 === '12') return ['OPEX', 'Other OpEx'];

  // ---- Other Income/Expense (13xxx, 14xxx, 15xxx) ----
  if (prefix2 === '13' || prefix2 === '14') return ['OTHER', 'Other Income/Expense'];
  if (code.startsWith('151')) return ['OTHER', 'Other Income'];
  if (code.startsWith('152')) return ['OTHER', 'Other Expenses'];
  if (prefix2 === '15') return ['OTHER', 'Other Income/Expense'];

  // ---- Balance Sheet: Assets (21xxx, 22xxx) ----
  if (prefix3 === '211' || prefix3 === '212' || prefix3 === '213') return ['ASSET_CURRENT', 'Cash & Bank'];
  if (prefix3 === '214') return ['ASSET_CURRENT', 'Receivables'];
  if (prefix3 === '215') return ['ASSET_CURRENT', 'Other Receivables'];
  if (prefix3 === '216') return ['ASSET_CURRENT', 'Inventory'];
  if (prefix3 === '217' || prefix3 === '218') return ['ASSET_CURRENT', 'Prepaid & Advances'];
  if (prefix3 === '219') return ['ASSET_CURRENT', 'Cash & Bank'];
  if (prefix3 === '221' || prefix3 === '222') return ['ASSET_FIXED', 'Fixed Assets Gross'];
  if (prefix3 === '223') return ['ASSET_FIXED', 'Accum Depreciation'];
  if (prefix3 === '224') return ['ASSET_OTHER', 'Other Assets'];
  if (prefix3 === '225') return ['ASSET_OTHER', 'Deferred Tax'];

  // ---- Liabilities (4xxxx) ----
  if (prefix3 === '411' || prefix3 === '412' || prefix3 === '413') return ['LIAB_CURRENT', 'Payables'];
  if (prefix3 === '421' || prefix3 === '420') return ['LIAB_CURRENT', 'Accrued Tax/BPJS'];
  if (prefix3 === '431' || prefix3 === '430') return ['LIAB_CURRENT', 'Accrued Salary'];
  if (prefix3 === '441' || prefix3 === '440') return ['LIAB_CURRENT', 'Accrued Expenses'];
  if (prefix3 === '451' || prefix3 === '450') return ['LIAB_LONGTERM', 'Long Term Liabilities'];

  // ---- Equity (3xxxx) ----
  if (code.startsWith('30000')) return ['EQUITY', 'Opening Balance Equity'];
  if (prefix2 === '30') return ['EQUITY', 'Equity'];
  if (prefix2 === '31' || prefix2 === '32' || prefix2 === '33') return ['EQUITY', 'Retained Earnings'];

  return ['UNMAPPED', 'UNMAPPED'];
}

// Deep clone utility
function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

// Dynamically decorate the static financial data module with localStorage adjustments
function applyAdjustments(baseData: DataModule, activeEntity: string): DataModule {
  const storageKey = activeEntity === 'JUARA_GADAI' ? 'juara_gadai_adjustments' : 'sahabat_gadai_adjustments';
  const rawAdjs = localStorage.getItem(storageKey);

  let adjustments: any[] = [];
  if (rawAdjs) {
    try {
      adjustments = JSON.parse(rawAdjs);
    } catch (e) {
      console.error("Failed to parse adjustments from localStorage:", e);
      adjustments = [];
    }
  }

  const hasAdjustments = adjustments.length > 0;

  // CRITICAL: we must ALWAYS rebuild the getter functions below so they close over
  // baseData's (dynamic, D1-sourced) data objects. baseData = { ...baseStatic, ...dynamicData };
  // the static module's getKPIs/etc close over the STATIC 24-month arrays, so returning
  // baseData directly would serve stale static data (and zeros for any new month like Mei '26)
  // even though baseData.MONTHS / baseData.profitLoss already hold the full dynamic dataset.
  //
  // Clone only when adjustments exist (we mutate the clones). With no adjustments, bind the
  // getters directly to baseData's objects — no clone needed, no mutation.
  const profitLoss = hasAdjustments ? deepClone(baseData.profitLoss) : baseData.profitLoss;
  const profitLossByBranch = hasAdjustments ? deepClone(baseData.profitLossByBranch) : baseData.profitLossByBranch;
  const balanceSheet = hasAdjustments ? deepClone(baseData.balanceSheet) : baseData.balanceSheet;
  const branchFullBS = hasAdjustments ? deepClone(baseData.branchFullBS) : baseData.branchFullBS;
  const bsBranch = hasAdjustments ? deepClone(baseData.bsBranch) : baseData.bsBranch;
  const cashFlow = hasAdjustments ? deepClone(baseData.cashFlow) : baseData.cashFlow;

  const MONTHS = baseData.MONTHS;
  const branchToArea = baseData.branchToArea;

  const recomputePL = (pl: any) => {
    const len = pl.revenue.total.length;
    pl.grossProfit = Array(len);
    pl.operatingProfit = Array(len);
    pl.netProfit = Array(len);
    for (let i = 0; i < len; i++) {
      pl.grossProfit[i] = pl.revenue.total[i] - pl.cogs.total[i];
      pl.operatingProfit[i] = pl.grossProfit[i] - pl.opex.total[i];
      pl.netProfit[i] = pl.operatingProfit[i] + pl.otherIncomeExpense[i];
    }
  };

  const recomputeBS = (bs: any) => {
    const len = bs.totalAssets.length;
    for (let i = 0; i < len; i++) {
      bs.currentAssets.total[i] = (bs.currentAssets.cashBank[i] || 0) +
        (bs.currentAssets.receivables[i] || 0) +
        (bs.currentAssets.prepaidAdvances[i] || 0) +
        (bs.currentAssets.inventory[i] || 0);

      bs.fixedAssets.net[i] = (bs.fixedAssets.gross[i] || 0) +
        (bs.fixedAssets.accumDepreciation[i] || 0);

      bs.totalAssets[i] = bs.currentAssets.total[i] +
        bs.fixedAssets.net[i] +
        (bs.otherAssets.deferredTax[i] || 0);

      bs.currentLiabilities.total[i] = (bs.currentLiabilities.payables[i] || 0) +
        (bs.currentLiabilities.accruedTax[i] || 0) +
        (bs.currentLiabilities.accruedSalary[i] || 0) +
        (bs.currentLiabilities.accruedExpenses[i] || 0);

      bs.totalLiabilities[i] = bs.currentLiabilities.total[i] +
        (bs.longTermLiabilities[i] || 0);

      bs.equity.total[i] = (bs.equity.openingBalance[i] || 0) +
        (bs.equity.retainedEarnings[i] || 0);
    }
  };

  // Apply each adjustment one by one
  for (const adj of adjustments) {
    const tgl = adj.tanggal;
    const moStr = tgl.slice(0, 7); // YYYY-MM
    const mIdx = MONTHS.findIndex(m => m.month === moStr);
    if (mIdx === -1) {
      continue;
    }

    const branchCode = adj.area; // e.g. "HO", "CIN"
    const branchArea = (branchToArea as any)[branchCode] || 'UNKNOWN';

    const debit = parseFloat(adj.debit) || 0;
    const credit = parseFloat(adj.credit) || 0;
    const amount = debit - credit;

    const [section, category] = classifyAccount(adj.no_acc);

    // 1. Process P&L Adjustment
    const applyPLChange = (pl: any, isBranch = false) => {
      if (!pl) return;
      if (section === 'REVENUE') {
        const change = credit - debit; // Credit-positive
        let subkey = 'interestIncome';
        if (category === 'Admin Fee') subkey = 'adminFee';
        else if (category === 'Gain Lelang') subkey = 'gainLelang';
        else if (category === 'Late Fee') subkey = 'lateFee';
        else if (category === 'Other Interest') subkey = 'otherInterest';
        else if (category === 'Accrued Revenue') subkey = 'accruedRevenue';

        pl.revenue[subkey][mIdx] += change;
        pl.revenue.total[mIdx] += change;
      } else if (section === 'COGS') {
        const change = debit - credit; // Debit-positive
        let subkey = 'repairLelang';
        if (category === 'Freight In') subkey = 'freightIn';
        else if (category === 'Bunga') subkey = 'interestExpense';

        pl.cogs[subkey][mIdx] += change;
        pl.cogs.total[mIdx] += change;
      } else if (section === 'OPEX') {
        const change = debit - credit; // Debit-positive
        let subkey = 'personnel';
        if (category === 'Office & Ops') subkey = 'officeOps';
        else if (category === 'Depresiasi') subkey = 'depreciation';
        else if (category === 'Travel') subkey = 'travel';
        else if (category === 'Other OpEx') subkey = 'otherOpex';

        pl.opex[subkey].total[mIdx] += change;
        pl.opex.total[mIdx] += change;

        // Add to items list if items exist
        const items = pl.opex[subkey].items || [];
        const item = items.find((x: any) => String(x.noAcc) === String(adj.no_acc));
        if (item) {
          item.values[mIdx] += change;
        } else {
          const newValues = Array(MONTHS.length).fill(0);
          newValues[mIdx] = change;
          items.push({
            noAcc: String(adj.no_acc),
            name: adj.name_acc,
            values: newValues
          });
        }
      } else if (section === 'OTHER') {
        const change = credit - debit; // Credit-positive
        pl.otherIncomeExpense[mIdx] += change;
      }

      recomputePL(pl);
    };

    // Apply to specific branch in P&L
    if (profitLossByBranch[branchCode]) {
      applyPLChange(profitLossByBranch[branchCode], true);
    }
    // Apply to area in P&L
    if ((profitLoss as any)[branchArea]) {
      applyPLChange((profitLoss as any)[branchArea]);
    }
    // Apply to ALL in P&L
    if (profitLoss['ALL']) {
      applyPLChange(profitLoss['ALL']);
    }

    // 2. Process Balance Sheet Adjustment
    const applyBSChange = (bs: any) => {
      if (!bs) return;
      if (section === 'ASSET_CURRENT') {
        let subkey = 'cashBank';
        if (category === 'Receivables') subkey = 'receivables';
        else if (category === 'Prepaid & Advances' || category === 'Other Receivables') subkey = 'prepaidAdvances';
        else if (category === 'Inventory') subkey = 'inventory';

        bs.currentAssets[subkey][mIdx] += amount;
      } else if (section === 'ASSET_FIXED') {
        let subkey = 'gross';
        if (category === 'Accum Depreciation') subkey = 'accumDepreciation';

        bs.fixedAssets[subkey][mIdx] += amount;
      } else if (section === 'ASSET_OTHER') {
        bs.otherAssets.deferredTax[mIdx] += amount;
      } else if (section === 'LIAB_CURRENT') {
        let subkey = 'payables';
        if (category === 'Accrued Tax/BPJS') subkey = 'accruedTax';
        else if (category === 'Accrued Salary') subkey = 'accruedSalary';
        else if (category === 'Accrued Expenses') subkey = 'accruedExpenses';

        // Credit-positive: add (credit - debit) = -amount
        bs.currentLiabilities[subkey][mIdx] -= amount;
      } else if (section === 'LIAB_LONGTERM') {
        bs.longTermLiabilities[mIdx] -= amount;
      } else if (section === 'EQUITY') {
        let subkey = 'openingBalance';
        if (category === 'Retained Earnings') subkey = 'retainedEarnings';

        bs.equity[subkey][mIdx] -= amount;
      }

      recomputeBS(bs);
    };

    // Apply to area BS
    if ((balanceSheet as any)[branchArea]) {
      applyBSChange((balanceSheet as any)[branchArea]);
    }
    // Apply to ALL BS
    if (balanceSheet['ALL']) {
      applyBSChange(balanceSheet['ALL']);
    }
    // Apply to branch full BS
    if (branchFullBS[branchCode]) {
      applyBSChange(branchFullBS[branchCode]);
    }

    // 3. Process bsBranch
    if (bsBranch[branchCode]) {
      if (section === 'ASSET_CURRENT') {
        if (category === 'Cash & Bank') {
          bsBranch[branchCode].cashBank[mIdx] += amount;
        } else if (category === 'Receivables') {
          bsBranch[branchCode].receivables[mIdx] += amount;
        }
      }
    }

    // 4. Process Cash Flow (Operating Cash Flow roll-forward)
    // P&L Net profit change is credit - debit
    const profitChange = credit - debit;
    const cfArea = (cashFlow as any)[branchArea] || cashFlow['ALL'];
    if (cfArea) {
      cfArea.operating.total[mIdx] += profitChange;
      cfArea.netChange[mIdx] += profitChange;
      for (let i = mIdx; i < cfArea.closingCash.length; i++) {
        cfArea.closingCash[i] += profitChange;
      }
    }
  }

  // SumRange Helper
  const sumRange = (arr: number[], s: number, e: number): number =>
    arr.slice(s, e + 1).reduce((a, b) => a + b, 0);

  const getPL = (key: string): any => {
    return (profitLossByBranch as any)[key] ?? (profitLoss as any)[key] ?? profitLoss['ALL'];
  };

  const getAreaForKey = (key: string): string => {
    return (branchToArea as any)[key] ?? key;
  };

  // Redeclare selector helper functions with local dynamic variables
  const getKPIs = (areaOrBranch: string, startIdx: number, endIdx: number) => {
    const pl = getPL(areaOrBranch);
    const cf = (cashFlow as any)[getAreaForKey(areaOrBranch)];
    return {
      totalRevenue: sumRange(pl.revenue.total, startIdx, endIdx),
      cogs: sumRange(pl.cogs.total, startIdx, endIdx),
      grossProfit: sumRange(pl.grossProfit, startIdx, endIdx),
      opex: sumRange(pl.opex.total, startIdx, endIdx),
      opProfit: sumRange(pl.operatingProfit, startIdx, endIdx),
      netProfit: sumRange(pl.netProfit, startIdx, endIdx),
      netCashFromOps: sumRange(cf.operating.total, startIdx, endIdx),
      closingCash: cf.closingCash[endIdx],
    };
  };

  const getRevenueExpensesSeries = (areaOrBranch: string, startIdx: number, endIdx: number) => {
    const pl = getPL(areaOrBranch);
    return {
      labels: MONTHS.slice(startIdx, endIdx + 1).map(m => m.label),
      revenue: pl.revenue.total.slice(startIdx, endIdx + 1),
      opex: pl.opex.total.slice(startIdx, endIdx + 1),
    };
  };

  const getExpenseBreakdown = (areaOrBranch: string, startIdx: number, endIdx: number) => {
    const pl = getPL(areaOrBranch);
    const items = [
      { category: 'Personnel',     value: sumRange(pl.opex.personnel.total,    startIdx, endIdx), color: '#10b981' },
      { category: 'Office & Ops',  value: sumRange(pl.opex.officeOps.total,    startIdx, endIdx), color: '#3b82f6' },
      { category: 'Depresiasi',    value: sumRange(pl.opex.depreciation.total, startIdx, endIdx), color: '#ec4899' },
      { category: 'Travel',        value: sumRange(pl.opex.travel.total,       startIdx, endIdx), color: '#f59e0b' },
      { category: 'Other OpEx',    value: sumRange(pl.opex.otherOpex.total,    startIdx, endIdx), color: '#a78bfa' },
      { category: 'Bunga',         value: sumRange(pl.cogs.interestExpense,    startIdx, endIdx), color: '#8b5cf6' },
      { category: 'Repair Lelang', value: sumRange(pl.cogs.repairLelang,       startIdx, endIdx), color: '#06b6d4' },
    ];
    return items.filter(x => Math.abs(x.value) > 0);
  };

  const getCashFlowSeries = (areaOrBranch: string, startIdx: number, endIdx: number) => {
    const cf = (cashFlow as any)[getAreaForKey(areaOrBranch)];
    return {
      labels: MONTHS.slice(startIdx, endIdx + 1).map(m => m.label),
      operating: cf.operating.total.slice(startIdx, endIdx + 1),
      investing: cf.investing.total.slice(startIdx, endIdx + 1),
      financing: cf.financing.total.slice(startIdx, endIdx + 1),
      net:       cf.netChange.slice(startIdx, endIdx + 1),
      closingCash: cf.closingCash.slice(startIdx, endIdx + 1),
    };
  };

  const getNetProfitSeries = (areaOrBranch: string, startIdx: number, endIdx: number) => {
    const pl = getPL(areaOrBranch);
    return {
      labels: MONTHS.slice(startIdx, endIdx + 1).map(m => m.label),
      netProfit: pl.netProfit.slice(startIdx, endIdx + 1),
    };
  };

  const getTopExpenses = (areaOrBranch: string, startIdx: number, endIdx: number, n: number = 10) => {
    const pl = getPL(areaOrBranch);
    const all = [
      { account: 'Personnel',     amount: sumRange(pl.opex.personnel.total,    startIdx, endIdx) },
      { account: 'Office & Ops',  amount: sumRange(pl.opex.officeOps.total,    startIdx, endIdx) },
      { account: 'Depresiasi',    amount: sumRange(pl.opex.depreciation.total, startIdx, endIdx) },
      { account: 'Travel',        amount: sumRange(pl.opex.travel.total,       startIdx, endIdx) },
      { account: 'Other OpEx',    amount: sumRange(pl.opex.otherOpex.total,    startIdx, endIdx) },
      { account: 'Bunga (COGS)',  amount: sumRange(pl.cogs.interestExpense,    startIdx, endIdx) },
      { account: 'Repair Lelang', amount: sumRange(pl.cogs.repairLelang,       startIdx, endIdx) },
      { account: 'Freight In',    amount: sumRange(pl.cogs.freightIn,          startIdx, endIdx) },
    ];
    return all.filter(x => Math.abs(x.amount) > 0)
              .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
              .slice(0, n);
  };

  const getPayablesReceivables = (areaOrBranch: string, idx: number) => {
    const area = getAreaForKey(areaOrBranch);
    const bs = (balanceSheet as any)[area];
    const ar = (baseData.accountReceivable as any)[area];
    return {
      payable: {
        accountsPayable: bs.currentLiabilities.payables[idx],
        accruedExpenses: bs.currentLiabilities.accruedExpenses[idx],
        otherPayable:    bs.currentLiabilities.accruedSalary[idx]
                       + bs.currentLiabilities.accruedTax[idx]
                       + bs.currentLiabilities.accruedBPJS[idx],
        total: bs.currentLiabilities.total[idx],
      },
      receivable: {
        accountsReceivable: bs.currentAssets.receivables[idx],
        otherReceivableSG:  bs.currentAssets.prepaidAdvances[idx],
        otherReceivableNseries: 0,
        total: ar.total[idx],
      },
    };
  };

  const getBranchBS = (branch: string, startIdx: number, endIdx: number) => {
    const bs = (bsBranch as any)[branch];
    if (!bs) return { cashBank: 0, receivables: 0 };
    return {
      cashBank:    sumRange(bs.cashBank,    startIdx, endIdx),
      receivables: sumRange(bs.receivables, startIdx, endIdx),
    };
  };

  const getBranchFullBSAtMonth = (branch: string, monthIdx: number) => {
    const bs = (branchFullBS as any)[branch];
    if (!bs) return null;
    return {
      currentAssets: {
        cashBank: bs.currentAssets.cashBank[monthIdx],
        receivables: bs.currentAssets.receivables[monthIdx],
        prepaidAdvances: bs.currentAssets.prepaidAdvances[monthIdx],
        otherReceivables: bs.currentAssets.otherReceivables[monthIdx],
        inventory: bs.currentAssets.inventory[monthIdx],
        total: bs.currentAssets.total[monthIdx],
      },
      fixedAssets: {
        gross: bs.fixedAssets.gross[monthIdx],
        accumDepreciation: bs.fixedAssets.accumDepreciation[monthIdx],
        net: bs.fixedAssets.net[monthIdx],
      },
      otherAssets: {
        deferredTax: bs.otherAssets.deferredTax[monthIdx],
      },
      totalAssets: bs.totalAssets[monthIdx],
      currentLiabilities: {
        payables: bs.currentLiabilities.payables[monthIdx],
        accruedTax: bs.currentLiabilities.accruedTax[monthIdx],
        accruedBPJS: bs.currentLiabilities.accruedBPJS[monthIdx],
        accruedSalary: bs.currentLiabilities.accruedSalary[monthIdx],
        accruedExpenses: bs.currentLiabilities.accruedExpenses[monthIdx],
        total: bs.currentLiabilities.total[monthIdx],
      },
      longTermLiabilities: bs.longTermLiabilities[monthIdx],
      totalLiabilities: bs.totalLiabilities[monthIdx],
      equity: {
        openingBalance: bs.equity.openingBalance[monthIdx],
        retainedEarnings: bs.equity.retainedEarnings[monthIdx],
        total: bs.equity.total[monthIdx],
      },
    };
  };

  const getBranchFullBSRange = (branch: string, startIdx: number, endIdx: number) => {
    const bs = (branchFullBS as any)[branch];
    if (!bs) return null;
    return {
      currentAssets: {
        cashBank: sumRange(bs.currentAssets.cashBank, startIdx, endIdx),
        receivables: sumRange(bs.currentAssets.receivables, startIdx, endIdx),
        prepaidAdvances: sumRange(bs.currentAssets.prepaidAdvances, startIdx, endIdx),
        otherReceivables: sumRange(bs.currentAssets.otherReceivables, startIdx, endIdx),
        inventory: sumRange(bs.currentAssets.inventory, startIdx, endIdx),
        total: sumRange(bs.currentAssets.total, startIdx, endIdx),
      },
      fixedAssets: {
        gross: sumRange(bs.fixedAssets.gross, startIdx, endIdx),
        accumDepreciation: sumRange(bs.fixedAssets.accumDepreciation, startIdx, endIdx),
        net: sumRange(bs.fixedAssets.net, startIdx, endIdx),
      },
      otherAssets: {
        deferredTax: sumRange(bs.otherAssets.deferredTax, startIdx, endIdx),
      },
      totalAssets: sumRange(bs.totalAssets, startIdx, endIdx),
      currentLiabilities: {
        payables: sumRange(bs.currentLiabilities.payables, startIdx, endIdx),
        accruedTax: sumRange(bs.currentLiabilities.accruedTax, startIdx, endIdx),
        accruedBPJS: sumRange(bs.currentLiabilities.accruedBPJS, startIdx, endIdx),
        accruedSalary: sumRange(bs.currentLiabilities.accruedSalary, startIdx, endIdx),
        accruedExpenses: sumRange(bs.currentLiabilities.accruedExpenses, startIdx, endIdx),
        total: sumRange(bs.currentLiabilities.total, startIdx, endIdx),
      },
      longTermLiabilities: sumRange(bs.longTermLiabilities, startIdx, endIdx),
      totalLiabilities: sumRange(bs.totalLiabilities, startIdx, endIdx),
      equity: {
        openingBalance: sumRange(bs.equity.openingBalance, startIdx, endIdx),
        retainedEarnings: sumRange(bs.equity.retainedEarnings, startIdx, endIdx),
        total: sumRange(bs.equity.total, startIdx, endIdx),
      },
    };
  };

  const getRevenueByBranch = (startIdx: number, endIdx: number) => {
    const result: any[] = [];
    const areaRevTotals: Record<string, number> = {};
    for (const area of baseData.AREAS) {
      areaRevTotals[area] = sumRange((profitLoss as any)[area].revenue.total, startIdx, endIdx);
    }
    for (const [branch, pl] of Object.entries(profitLossByBranch as any)) {
      const plData = (pl as any);
      const area = (branchToArea as any)[branch] || 'UNKNOWN';
      const monthlyData = plData.revenue.total.slice(startIdx, endIdx + 1) as number[];
      const totalPeriod = monthlyData.reduce((a: number, b: number) => a + b, 0);
      const avgPerMonth = totalPeriod / monthlyData.length;
      const pctOfArea = totalPeriod / areaRevTotals[area] * 100;

      const firstMonth = monthlyData[0];
      const lastMonth = monthlyData[monthlyData.length - 1];
      let trend: 'up' | 'down' | '-' = '-';
      if (firstMonth > 0 && lastMonth > firstMonth) trend = 'up';
      else if (firstMonth > 0 && lastMonth < firstMonth) trend = 'down';

      result.push({ branch, area, totalPeriod, avgPerMonth, pctOfArea, trend, monthlyData });
    }
    return result.sort((a, b) => (branchToArea as any)[a.branch]?.localeCompare((branchToArea as any)[b.branch] || '') || 0);
  };

  const getExpensesByBranch = (startIdx: number, endIdx: number) => {
    const result: any[] = [];
    const areaOpexTotals: Record<string, number> = {};
    for (const area of baseData.AREAS) {
      areaOpexTotals[area] = sumRange((profitLoss as any)[area].opex.total, startIdx, endIdx);
    }
    for (const [branch, pl] of Object.entries(profitLossByBranch as any)) {
      const plData = (pl as any);
      const area = (branchToArea as any)[branch] || 'UNKNOWN';
      const monthlyData = plData.opex.total.slice(startIdx, endIdx + 1) as number[];
      const totalPeriod = monthlyData.reduce((a: number, b: number) => a + b, 0);
      const avgPerMonth = totalPeriod / monthlyData.length;
      const pctOfArea = totalPeriod / areaOpexTotals[area] * 100;

      const firstMonth = monthlyData[0];
      const lastMonth = monthlyData[monthlyData.length - 1];
      let trend: 'up' | 'down' | '-' = '-';
      if (firstMonth > 0 && lastMonth > firstMonth) trend = 'up';
      else if (firstMonth > 0 && lastMonth < firstMonth) trend = 'down';

      result.push({ branch, area, totalPeriod, avgPerMonth, pctOfArea, trend, monthlyData });
    }
    return result.sort((a, b) => (branchToArea as any)[a.branch]?.localeCompare((branchToArea as any)[b.branch] || '') || 0);
  };

  return {
    ...baseData,
    profitLoss,
    profitLossByBranch,
    balanceSheet,
    branchFullBS,
    bsBranch,
    cashFlow,
    getKPIs,
    getRevenueExpensesSeries,
    getExpenseBreakdown,
    getCashFlowSeries,
    getNetProfitSeries,
    getTopExpenses,
    getPayablesReceivables,
    getBranchBS,
    getBranchFullBSAtMonth,
    getBranchFullBSRange,
    getRevenueByBranch,
    getExpensesByBranch,
  };
}

export const DataProvider: React.FC<DataProviderProps> = ({ activeEntity, children }) => {
  const [dynamicData, setDynamicData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [updateTrigger, setUpdateTrigger] = useState(0);

  useEffect(() => {
    const handleChanged = () => {
      setUpdateTrigger(prev => prev + 1);
    };
    window.addEventListener('financial-data-changed', handleChanged);
    return () => {
      window.removeEventListener('financial-data-changed', handleChanged);
    };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const API_KEY = process.env.REACT_APP_BUDGET_API_KEY ?? '';
    const API_BASE = process.env.REACT_APP_BUDGET_API_BASE ?? '';

    fetch(`${API_BASE}/api/financial-data?entity=${activeEntity}`, {
      headers: {
        'Authorization': `Bearer ${API_KEY}`
      }
    })
      .then(res => {
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        return res.json();
      })
      .then(liveData => {
        if (active) {
          console.log(`Loaded live financial data for ${activeEntity} from D1 database. Months: ${liveData.MONTHS?.length || 0}, FY_PRESETS: ${Object.keys(liveData.FY_PRESETS || {}).length}`);
          setDynamicData(liveData);
        }
      })
      .catch(err => {
        console.warn(`Could not load live financial data for ${activeEntity} from D1 (falling back to static bundle):`, err);
        if (active) {
          setDynamicData(null);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [activeEntity, updateTrigger]);

  const data = useMemo<DataModule>(() => {
    const baseStatic = activeEntity === 'JUARA_GADAI' ? JuaraGadaiData : SahabatGadaiData;
    const baseData = dynamicData ? { ...baseStatic, ...dynamicData } : baseStatic;
    return applyAdjustments(baseData as DataModule, activeEntity);
  }, [activeEntity, dynamicData]);

  return <DataContext.Provider value={data}>{children}</DataContext.Provider>;
};

export function useData(): DataModule {
  return useContext(DataContext);
}

export default DataContext;
